<template>
  <div class="register-container employee-register">
    <div class="content-form register-form">
      <div
        v-for="(line) in orderedLineList"
        v-show="line.show"
        class="form-line"
        :key="line.name"
        :class="line.hasError && 'has-error'"
      >
        <el-select
          v-if="line.name === 'registerCompany'"
          v-model="line.value"
          class="line-select"
          :popper-class="isMobile ? 'is-mobile-el-select' : ''"
          :placeholder="line.placeholder"
          @focus="clearErrorMessage(line)"
          @blur="validate(line)"
          @change="validate(line)"
        >
          <el-option
            v-for="(com) in employeeForm.register_company_list"
            :key="com.id"
            :label="com.i18nName"
            :value="com.id"
          ></el-option>
        </el-select>
        <el-autocomplete
          v-else-if="line.name === 'email'"
          v-model="line.value"
          class="line-input email"
          :popper-class="isMobile ? 'is-mobile-el-select' : ''"
          :placeholder="line.placeholder"
          :fetch-suggestions="getEmailSuggestions"
          :trigger-on-focus="false"
          @focus="clearErrorMessage(line)"
          @input="validate(line)"
        ></el-autocomplete>
        <el-input
          v-else
          v-model="line.value"
          class="line-input"
          :autocomplete="line.name==='password' ? 'new-password' : 'false'"
          :class="line.name === 'password' && 'password'"
          :type="(line.name !== 'password' || viewPassword) ? 'text' : 'password'"
          :placeholder="line.placeholder"
          @focus="clearErrorMessage(line)"
          @blur="validate(line)"
          @change="validate(line)"
        >
          <template v-if="line.name === 'password'" #suffix>
            <i class="password-icon iconfont" :class="viewPassword ? 'iconVisible' : 'iconHide1'" @click="onViewPassword"></i>
          </template>
        </el-input>
        <div v-show="line.message.show" class="line-tip">{{ line.message.textI18nKey ? $t(line.message.textI18nKey) : line.message.text }}</div>
      </div>
    </div>
    <div class="button-container">
      <el-button class="register-btn" type="primary" @click="onRegisterBtnClick">{{ $t('login.common.btn.register.employee') }}</el-button>
    </div>
    <div class="form-bottom">
      <div v-show="showRegisterTip" class="bottom-line">
        <div class="register-tip">{{ $t('login.common.message.employeeRegisterTip') }}</div>
      </div>
      <div class="bottom-line">
        <el-checkbox v-model="agree" class="policy-checkbox">
          <span>{{ $t('login.common.message.policy.tip') }}</span>
          <a class="link" href="/tos/" target="_blank">{{ $t('login.common.message.policy.userAgreement') }}</a>
          <span> & </span>
          <a class="link" href="/policy/" target="_blank">{{ $t('login.common.message.policy.privacyPolicy') }}</a>
        </el-checkbox>
        <div class="line-tip"></div>
      </div>
      <div v-if="employeeForm.custom_policy" class="bottom-line">
        <el-checkbox v-model="agreeCustomPolicy" class="policy-checkbox company-custom-policy">
          <span>{{ employeeForm.custom_policy.i18nTip }}</span>
          <a class="link" :href="employeeForm.custom_policy.i18nLink" target="_blank">{{ employeeForm.custom_policy.i18nLabel }}</a>
        </el-checkbox>
        <div class="line-tip"></div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { ACCOUNT_TYPE } from '@/constant/common'
import { addI18nProperty } from '@/i18n'
import { mixedRouterPush } from '@/router/routes'
import { validateEmail, validatePassword } from '@/utils/validator'
import { deviceType } from '@/utils/common'
import { passwordEncrypt } from '@/utils/rsa'
import { ROOT_COMPANY_ID } from '@/constant/company'

export default {
  props: {
    company: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      ACCOUNT_TYPE,
      isMobile: deviceType.isMobile,
      formData: {
        registerCompany: null,
        name: null,
        email: null,
        employeeGroupIdNum: null,
        employeeIdNum: null,
        phone: null,
        password: null,
        authInfo: null,
        pinCode: null
      },
      agree: false,
      agreeCustomPolicy: false,
      formLineOrder: ['name', 'email', 'password', 'authInfo', 'pinCode'],
      formLineMax: {
        name: 128,
        employeeGroupIdNum: 30,
        employeeIdNum: 128,
        authInfo: 30,
        pinCode: 8
      },
      message: {
        registerCompany: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        },
        name: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        },
        email: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        },
        employeeGroupIdNum: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        },
        employeeIdNum: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        },
        phone: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        },
        password: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        },
        authInfo: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        },
        pinCode: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        }
      },
      viewPassword: false,
      customPlaceholder: {},
      customErrorMessage: {},
      currentRegisterCompanyId: null
    }
  },
  computed: {
    accountType () {
      return this.$route.meta?.accountType || this.$route.params.accountType
    },
    employeeForm () {
      return this.company?.employee_form
    },
    showRegisterTip () {
      if (this.company.root_company_id === ROOT_COMPANY_ID.HSBC) {
        return false
      }
      return !this.employeeForm?.is_customization_form && !this.employeeForm?.hide_active_email_tip && !this.showAuthInfoOrPinCode()
    },
    orderedLineList () {
      const vm = this
      return _.map(this.formLineOrder, lineName => {
        const line = {
          name: lineName,
          show: true,
          get value () { return vm.formData[lineName] },
          set value (val) { vm.$set(vm.formData, lineName, val) },
          get placeholder () { return vm.customPlaceholder[lineName] || vm.$t(`login.common.label.employeeRegister.${this.name}`) },
          get message () { return vm.message[lineName] },
          get hasError () { return this.message && this.message.show && this.message.isError }
        }
        if (['pinCode', 'authInfo'].includes(lineName)) {
          Object.defineProperty(line, 'show', {
            configurable: true,
            enumerable: true,
            get: this.showAuthInfoOrPinCode
          })
        }
        return line
      })
    },
    showLines () {
      return _.map(this.orderedLineList, 'name')
    }
  },
  created () {
    this.initPage()
  },
  methods: {
    ...mapActions('account', ['checkSSO', 'registerEmployee']),
    async initPage () {
      this.$eventBus.$emit('showLoading', true)
      try {
        if (!this.company?.id) {
          this.$router.push('/error/404.html')
          return
        }
        const resp = await this.checkSSO({
          account_type: this.accountType,
          employee_company_id: this.company.id,
          back: this.getBackUrl()
        })
        if (resp && resp.sso_path) {
          window.location.href = resp.sso_path
          return
        }
        const employeeForm = this.employeeForm
        this.formLineOrder = _.map(employeeForm.form_line_order || this.formLineOrder, val => _.camelCase(val))
        _.forEach(employeeForm.custom_placeholder, (placeholder, lineName) => {
          lineName = _.camelCase(lineName)
          addI18nProperty(this.customPlaceholder, lineName, 'cn', 'en', placeholder)
        })
        _.forEach(employeeForm.custom_required_tip, (msg, lineName) => {
          lineName = _.camelCase(lineName)
          addI18nProperty(this.customErrorMessage, lineName, 'cn', 'en', msg)
        })
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    showAuthInfoOrPinCode () {
      if (!this.formLineOrder.includes('pinCode') && !this.formLineOrder.includes('authInfo')) {
        return false
      }
      if (!this.formData.email) {
        return false
      }
      const emailAddress = _.split(this.formData.email, '@')
      if (emailAddress.length === 2) {
        const emailAddressSuffix = _.toLower(emailAddress[1])
        // url 上的 company 配置的邮箱后缀
        const companyEmailSuffixList = this.company.email_suffix
        let emailSuffix = []
        // 选择了公司则使用选择的公司配置的
        if (this.currentRegisterCompanyId) {
          const registerCompany = _.find(this.employeeForm.register_company_list, { id: this.currentRegisterCompanyId })
          emailSuffix = registerCompany.email_suffix
        }
        // 没有 suffixList 则使用 url 上的 company 配置的邮箱后缀
        if (!emailSuffix?.length) {
          emailSuffix = companyEmailSuffixList
        }
        if (emailSuffix.includes(emailAddressSuffix)) {
          return false
        }
      }
      return true
    },
    onViewPassword () {
      this.viewPassword = !this.viewPassword
    },
    setErrorMessage (message, textI18nKey, getter) {
      if (getter) {
        Object.defineProperty(message, 'text', {
          configurable: true,
          enumerable: true,
          get: getter
        })
      } else {
        message.text = null
      }
      message.isError = true
      message.textI18nKey = textI18nKey
      message.show = true
    },
    clearErrorMessage (line) {
      const message = line.message
      message.show = false
      message.isError = false
      message.text = null
      message.textI18nKey = null
    },
    validate (line) {
      if (!line) {
        return !_.chain(this.orderedLineList).map(this.validate).includes(false).value()
      }
      let hasError = false
      let textI18nKey = null
      let getter = null
      const formData = this.formData
      const lineName = line.name
      const data = formData[lineName]
      if (lineName === 'registerCompany') {
        this.currentRegisterCompanyId = line.value
      }
      if (!data) {
        if (['authInfo', 'pinCode'].includes(lineName)) {
          hasError = this.showAuthInfoOrPinCode()
        } else {
          hasError = true
        }
        getter = () => {
          if (this.customErrorMessage[lineName]) {
            return this.customErrorMessage[lineName]
          }
          const placeholder = line.placeholder
          if (placeholder.startsWith('Please') || placeholder.startsWith('Select')) {
            return placeholder
          }
          if (this.$te(`login.common.placeholder.employeeRegister.${lineName}`)) {
            return this.$t(`login.common.placeholder.employeeRegister.${lineName}`)
          }
          return this.$t('login.common.placeholder.pleaseEnter', { placeholder: line.placeholder })
        }
      }
      if (!hasError) {
        if (lineName === 'email' && validateEmail(null, data)) {
          hasError = true
          textI18nKey = 'common.message.invalidEmail'
        } else if (lineName === 'password' && validatePassword(null, data)) {
          hasError = true
          textI18nKey = 'login.common.message.passwordRule'
        } else if (lineName === 'employeeIdNum' && this.employeeForm.is_customization_form && data.includes('@')) {
          hasError = true
          textI18nKey = 'login.company.register.employee.message.invalidEmployeeId'
        }
      }
      const max = this.formLineMax[lineName]
      if (!hasError && max && data && data.length > max) {
        hasError = true
        getter = () => this.$t('common.message.InvalidMaxLength', { max })
      }
      if (hasError) {
        this.setErrorMessage(line.message, textI18nKey, getter)
        return false
      } else {
        this.clearErrorMessage(line)
        return true
      }
    },
    async onRegisterBtnClick () {
      if (!this.agree || (this.employeeForm.custom_policy && !this.agreeCustomPolicy)) {
        this.$message.warning(this.$t('login.common.message.agreePolicyFirst'))
        return
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        if (!this.validate()) {
          return
        }
        const formData = this.formData
        const employeeInfo = {}
        _.forEach(this.orderedLineList, line => {
          const lineName = line.name
          const key = _.snakeCase(lineName)
          let data = formData[lineName]
          if (['name', 'email', 'employeeIdNum', 'authInfo', 'pinCode', 'employeeGroupIdNum', 'phone'].includes(lineName)) {
            data = data && data.trim()
          }
          employeeInfo[key] = data
        })
        employeeInfo.password = passwordEncrypt(employeeInfo.password)
        const redirectCompanyId = this.$route.query.redirect_company_id
        const redirectJobId = this.$route.query.redirect_job_id
        employeeInfo.redirect_company_id = redirectCompanyId
        employeeInfo.redirect_job_id = redirectJobId
        const resp = await this.registerEmployee({ employeeInfo, companyId: this.company.id })
        if (resp) {
          const nextPage = this.getBackUrl() || resp.next_page
          mixedRouterPush(nextPage)
        }
      } catch (err) {
        const errorCode = err.code
        // const message = this.message
        if (errorCode) {
          if (errorCode === 20006004) {
            if (this.showLines.includes('employeeIdNum')) {
              // this.setErrorMessage(message.employeeIdNum, 'login.company.register.employee.message.employeeIdNotMatchedEmail')
              // this.setErrorMessage(message.email, 'login.company.register.employee.message.employeeIdNotMatchedEmail')
              this.$message.error(this.$t('login.company.register.employee.message.employeeIdNotMatchedEmail'))
            } else {
              // this.setErrorMessage(message.name, 'login.company.register.employee.message.nameNotMatchedEmail')
              // this.setErrorMessage(message.email, 'login.company.register.employee.message.nameNotMatchedEmail')
              this.$message.error(this.$t('login.company.register.employee.message.nameNotMatchedEmail'))
            }
          } else if (errorCode === 20006005) {
            // this.setErrorMessage(message.employeeIdNum, 'login.company.register.employee.message.employeeIdHasBeenUsed')
            // this.setErrorMessage(message.email, 'login.company.register.employee.message.employeeIdHasBeenUsed')
            this.$message.error(this.$t('login.company.register.employee.message.employeeIdHasBeenUsed'))
          } else if (errorCode === 20006006) {
            // this.setErrorMessage(message.pinCode, 'login.company.register.employee.message.invalidPinCode')
            this.$message.error(this.$t('login.company.register.employee.message.invalidPinCode'))
          }
        }
        throw err
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    getEmailSuggestions (value, callback) {
      // url 上的 company 配置的邮箱后缀
      const companyEmailSuffixList = this.company.email_suffix
      let suffixList = []
      // 选择了公司则使用选择的公司配置的
      if (this.currentRegisterCompanyId) {
        const registerCompany = _.find(this.employeeForm.register_company_list, { id: this.currentRegisterCompanyId })
        suffixList = registerCompany.email_suffix
      }
      // 没有 suffixList 则使用 url 上的 company 配置的邮箱后缀
      if (!suffixList?.length) {
        suffixList = companyEmailSuffixList
      }
      let suggestionList = _.chain(suffixList)
        .filter(suffix => suffix)
        .map(suffix => ({ value: `${value}@${suffix}` }))
        .value()

      const emailAddress = _.split(value, '@')
      if (emailAddress.length >= 2) {
        const emailAddressSuffix = _.toLower(emailAddress[1])
        suggestionList = _.chain(suffixList)
          .filter(suffix => _.includes(suffix, emailAddressSuffix))
          .map(suffix => ({ value: `${emailAddress[0]}@${suffix}` }))
          .value()
      }
      callback(suggestionList)
    },
    getBackUrl () {
      const query = this.$route.query
      return query.back ? `${query.back}${this.$route.hash}` : null
    }
  }
}
</script>

<style lang="scss" scoped>
.register-container {
  .register-form {
    margin-top: 0;

    .form-line {
      min-height: 40px;
      margin-bottom: 24px;

      &.has-error {
        margin-bottom: 6px;

        .line-tip {
          padding-left: 0;
          color: #f56c6c;
        }

        .line-input {
          ::v-deep {
            > .el-input__inner {
              border-color: #f56c6c;
            }

            .el-input__inner:hover + .el-input-group__prepend,
            .el-input__inner:hover + .el-input-group__append,
            .el-input-group__prepend,
            .el-input-group__append {
              border-color: #f56c6c;
            }
          }
        }
      }

      .sub-form-line {
        display: inline-block;
        width: 200px;
        height: 40px;
        vertical-align: middle;

        &.has-error {
          .line-tip {
            padding-left: 0;
            color: #f56c6c;
          }

          .line-input {
            ::v-deep {
              .el-input__inner {
                border-color: #f56c6c;
              }
            }
          }
        }

        + .sub-form-line {
          margin-left: 20px;
        }
      }

      .line-select {
        width: 100%;
      }

      .line-input {
        &.password {
          ::v-deep {
            .el-input__inner {
              padding-right: 35px;
            }
          }

          .password-icon {
            height: 40px;
            line-height: 40px;
            cursor: pointer;
            font-size: 14px;
            color: #909399;
          }
        }

        &.email {
          width: 100%;
        }

        ::v-deep {
          .el-input__inner {
            border-radius: 2px;
            padding-left: 14px;
          }

          .el-input__suffix {
            right: 14px;
          }

          .el-input-group__prepend {
            border-top-left-radius: 2px;
            border-bottom-left-radius: 2px;
            background: initial;
            transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)
          }

          .el-input-group__append {
            border-top-right-radius: 2px;
            border-bottom-right-radius: 2px;
            background: initial;
            transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)
          }
        }
      }

      .line-tip {
        padding-left: 15px;
        font-size: 12px;
        line-height: 18px;
      }
    }
  }

  .button-container {

    .el-button {
      width: 100%;
      padding: 9px 0;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      text-align: center;
      border: 0;
      background-color: var(--custom-color, #0096cc);
    }
  }

  .form-bottom {
    margin-top: 20px;

    .bottom-line {
      margin-bottom: 10px;

      .policy-checkbox {
        white-space: nowrap;

        .link {
          color: var(--custom-color, #0096cc);
        }

        ::v-deep {
          .el-checkbox__inner {
            width: 12px;
            height: 12px;

            &::after {
              top: 0;
              left: 3px;
            }
          }

          &.is-focus .el-checkbox__inner {
            border-color: var(--custom-color, #0096cc);
          }

          &.is-checked .el-checkbox__inner {
            background-color: var(--custom-color, #0096cc);
            border-color: var(--custom-color, #0096cc);
          }

          .el-checkbox__label {
            white-space: initial;
            word-break: break-word;
            vertical-align: text-top;
            color: #606266;
            font-size: 12px;
            line-height: 19px;
          }
        }
      }

      .register-tip {
        font-size: 12px;
        line-height: 17px;
        margin-bottom: 14px;
      }
    }
  }
}
.is-mobile {
  .register-container {
    .container-title {
      font-size: 30px;
      line-height: 44px;
    }
  }
}
</style>

  <style lang="scss">
  // to fix 在ios设备中，el-select组件下拉框，点击2次才能选中问题。
  .is-mobile-el-select {
    .el-scrollbar {
      .el-scrollbar__bar {
        opacity: 1 !important;
      }
    }
  }
  </style>
